// ============================================================================
// CONFIGURATION REPORT (Setup > Configuration Report)
// ============================================================================
// crs-doc/CRS-Setup-Navigation-and-Help-Design.md S8, section 6: the tenant's setup in Setup order and the
// setup changes since a date, for review and sign-off. The server builds one report; this screen, the Excel
// workbook (a sheet per section) and the PDF (with sign-off lines) all show it, so they cannot disagree.

import React, { useCallback, useEffect, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import notificationService from 'services/notificationService';
import ReportHeader, { type ReportHeaderItem } from 'components/reports/ReportHeader';
import { setupService, type ConfigReport } from 'api/services/setupService';

interface ConfigReportScreenProps {
  tabId?: string;
  moduleName?: string;
  allowedActions?: string[];
}

const isoDaysAgo = (days: number) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
};

const ConfigReportScreen: React.FC<ConfigReportScreenProps> = () => {
  const [since, setSince] = useState(isoDaysAgo(30));
  const [report, setReport] = useState<ConfigReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [section, setSection] = useState<string>('');
  const [downloading, setDownloading] = useState<'excel' | 'pdf' | null>(null);

  const load = useCallback(async (changedSince: string) => {
    setLoading(true);
    try {
      setReport(await setupService.getConfigReport(changedSince));
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || err?.message || 'The configuration report could not be prepared.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(since); }, [load, since]);

  const download = async (kind: 'excel' | 'pdf') => {
    setDownloading(kind);
    try {
      await setupService.downloadConfigReport(kind, since);
    } catch (err: any) {
      notificationService.toast.error(err?.message || 'The file could not be downloaded.');
    } finally {
      setDownloading(null);
    }
  };

  const headerItems: ReportHeaderItem[] = [
    { label: 'Report', value: 'Configuration', primary: true },
    { label: 'Changes since', value: since, primary: true, icon: 'calendar' },
    ...(report ? [
      { label: 'Generated', value: new Date(report.generatedAt).toLocaleString(), icon: 'clock' as const },
      ...(report.generatedBy ? [{ label: 'By', value: report.generatedBy, icon: 'user' as const }] : []),
    ] : []),
  ];

  const sections = (report?.sections ?? []).filter((s) => !section || s.key === section);

  return (
    <div className="module-master-container">
      <ReportHeader items={headerItems} />
      <div className="setup-sheet">
        <div className="config-panel-header setup-checklist-header">
          <div className="setup-checklist-tools">
            <label className="setup-checklist-toggle">
              Changes since
              <input type="date" className="setup-sheet-select" style={{ minWidth: 0 }} value={since}
                max={new Date().toISOString().slice(0, 10)} onChange={(e) => e.target.value && setSince(e.target.value)} />
            </label>
            <select className="setup-sheet-select" value={section} onChange={(e) => setSection(e.target.value)}>
              <option value="">Every section</option>
              {(report?.sections ?? []).map((s) => <option key={s.key} value={s.key}>{s.title}</option>)}
            </select>
          </div>
          <div className="setup-checklist-tools">
            <button className="btn btn-secondary btn-sm" onClick={() => void load(since)} disabled={loading} title="Prepare again">
              <IconSystem.refresh size={16} />
            </button>
            <button className="btn btn-secondary btn-sm" onClick={() => void download('excel')} disabled={downloading !== null}>
              <IconSystem.download size={16} /> {downloading === 'excel' ? 'Preparing...' : 'Excel'}
            </button>
            <button className="btn btn-primary btn-sm" onClick={() => void download('pdf')} disabled={downloading !== null}>
              <IconSystem.print size={16} /> {downloading === 'pdf' ? 'Preparing...' : 'PDF for sign-off'}
            </button>
          </div>
        </div>

        {loading && !report && <div className="config-loading">Preparing the report...</div>}

        {sections.map((s) => (
          <section key={s.key} className="setup-sheet-section">
            <h3>{s.title}</h3>
            {s.tables.map((t) => (
              <div key={t.title} className="config-report-table">
                <div className="setup-sheet-note config-report-table-title">{t.title}</div>
                {t.rows.length === 0 ? (
                  <div className="setup-sheet-note">None.</div>
                ) : (
                  <div className="config-report-scroll">
                    <table className="setup-sheet-table">
                      <thead><tr>{t.columns.map((c) => <th key={c}>{c}</th>)}</tr></thead>
                      <tbody>
                        {t.rows.map((r, i) => (
                          <tr key={i}>
                            {t.columns.map((_, ci) => (
                              <td key={ci} style={r.marked.includes(ci) ? { fontWeight: 700 } : undefined}>{r.cells[ci] ?? ''}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {t.note && <div className="setup-sheet-note"><em>{t.note}</em></div>}
              </div>
            ))}
          </section>
        ))}
      </div>
    </div>
  );
};

export default ConfigReportScreen;
